import styled, {css, keyframes} from "styled-components";

/**
 * SmeltingUnlockModal styled components and animations
 *
 * This module contains all styled components and keyframe animations
 * used in the smelting unlocking mini-games (temperature control, metal pouring, brick inspection).
 */

export const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

export const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const fill = keyframes`
  from { height: 0%; }
  to { height: 100%; }
`;

export const highlight = keyframes`
  from { filter: brightness(1); }
  50% { filter: brightness(1.5); }
  to { filter: brightness(1); }
`;

// Temperature Control Game Styles

export const TemperatureGameContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 20px;
`;

export const TemperatureGauge = styled.div<{ $greenMin: number, $greenMax: number }>`
  width: 300px;
  height: 150px;
  position: relative;
  border-radius: 150px 150px 0 0;
  overflow: hidden;
  border: 4px solid #333;
  background: linear-gradient(to right,
    #4a90e2 0%,
    #4a90e2 ${props => props.$greenMin}%,
    #50c878 ${props => props.$greenMin}%,
    #50c878 ${props => props.$greenMax}%,
    #e74c3c ${props => props.$greenMax}%,
    #e74c3c 100%
  );
`;

export const TemperatureNeedle = styled.div<{ $angle: number }>`
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 4px;
  height: 120px;
  background: #333;
  transform-origin: bottom center;
  transform: translateX(-50%) rotate(${props => props.$angle}deg);
  transition: transform 0.05s linear;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 12px;
    height: 12px;
    background: #e74c3c;
    border-radius: 50%;
  }
`;

export const AirflowBar = styled.div`
  width: 400px;
  height: 40px;
  background: #ddd;
  border: 3px solid #333;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
`;

export const AirflowFill = styled.div<{ $percentage: number }>`
  height: 100%;
  width: ${props => props.$percentage}%;
  background: linear-gradient(to right, #4a90e2, #2980b9);
  transition: width 0.2s ease-out;
`;

export const WheelContainer = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

export const Wheel = styled.div<{ $rotation: number }>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle, #bbb, #888);
  border: 8px solid #555;
  position: relative;
  transform: rotate(${props => props.$rotation}deg);
  transition: transform 0.1s ease-out;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 20px;
    height: 20px;
    background: #333;
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }

  &::after {
    content: '';
    position: absolute;
    top: 10%;
    left: 50%;
    width: 4px;
    height: 40%;
    background: #333;
    transform: translateX(-50%);
  }
`;

export const StatusText = styled.div<{ $success?: boolean, $failure?: boolean }>`
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.$success ? '#50c878' : props.$failure ? '#e74c3c' : '#333'};
  text-align: center;
  min-height: 24px;
`;

// Metal Pouring Game Styles

export const PouringGameContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 20px;
`;

export const Crucible = styled.div<{ $pouring: boolean }>`
  width: 120px;
  height: 100px;
  background: linear-gradient(to bottom, #ff6b35, #f7931e);
  border: 4px solid #8b4513;
  border-radius: 0 0 60px 60px;
  position: relative;
  box-shadow: inset 0 -10px 20px rgba(0, 0, 0, 0.3);
  animation: ${props => props.$pouring ? css`${pulse} 0.5s ease-in-out infinite` : 'none'};
`;

export const MoldConveyor = styled.div`
  width: 500px;
  height: 120px;
  background: #555;
  border: 3px solid #333;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.4);
`;

export const Mold = styled.div<{ $fullness: number, $target: number, $complete: boolean, $failed: boolean }>`
  width: 80px;
  height: 100px;
  background: #888;
  border: 3px solid #333;
  border-radius: 4px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.4);
  border-color: ${props =>
    props.$complete ? '#50c878' :
    props.$failed ? '#e74c3c' : '#333'};
`;

export const MoldFill = styled.div<{ $fullness: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${props => props.$fullness}%;
  background: linear-gradient(to bottom, #ff6b35, #d84315);
  transition: height 0.1s linear;
`;

export const TargetLine = styled.div<{ $target: number }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${props => props.$target}%;
  height: 2px;
  background: #50c878;
  transform: translateY(50%);
  box-shadow: 0 0 4px #50c878;
`;

export const ToleranceZone = styled.div<{ $target: number, $tolerance: number }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${props => props.$target - props.$tolerance / 2}%;
  height: ${props => props.$tolerance}%;
  background: rgba(80, 200, 120, 0.2);
  border-top: 1px dashed rgba(80, 200, 120, 0.5);
  border-bottom: 1px dashed rgba(80, 200, 120, 0.5);
`;

export const ProgressCounter = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #333;
`;

// Brick Inspection Game Styles

export const InspectionGameContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px;
`;

export const BrickGrid = styled.div<{ $gridSize: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$gridSize}, 1fr);
  gap: 2px;
  background: #555;
  padding: 4px;
  padding-left: ${props => Math.max(14, Math.min(24, 304 / props.$gridSize))}px;
  padding-right: ${props => Math.max(14, Math.min(24, 304 / props.$gridSize))}px;
  border-radius: 8px;
  max-width: ${props => 600 + Math.max(20, Math.min(40, 600 / props.$gridSize))}px;
  position: relative;
  overflow: visible;
`;

export const Brick = styled.button<{ $broken: boolean, $clicked: boolean, $row: number, $gridSize: number }>`
  width: ${props => Math.max(20, Math.min(40, 600 / props.$gridSize))}px;
  height: ${props => Math.max(20, Math.min(40, 600 / props.$gridSize))}px;
  background: ${props => props.$clicked ? '#50c878' : 'hsl(20, 40%, 45%)'};
  border: 1px solid #333;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  transform: ${props => props.$row % 2 === 1 ? `translateX(${Math.max(10, Math.min(20, 300 / props.$gridSize))}px)` : 'translateX(0)'};

  &:hover {
    filter: brightness(1.2);
    transform: ${props => props.$row % 2 === 1 ? `translateX(${Math.max(10, Math.min(20, 300 / props.$gridSize))}px) scale(1.05)` : 'scale(1.05)'};
  }

  &:active {
    transform: ${props => props.$row % 2 === 1 ? `translateX(${Math.max(10, Math.min(20, 300 / props.$gridSize))}px) scale(0.95)` : 'scale(0.95)'};
  }

  ${props => props.$clicked && css`
    animation: ${highlight} 0.5s ease-in-out;
  `}

  /* Show cross on hover for broken bricks */
  ${props => props.$broken && !props.$clicked && css`
    &:hover::before,
    &:hover::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 3px;
      height: 120%;
      background: #e74c3c;
      box-shadow: 0 0 4px rgba(231, 76, 60, 0.8);
    }

    &:hover::before {
      transform: translate(-50%, -50%) rotate(45deg);
    }

    &:hover::after {
      transform: translate(-50%, -50%) rotate(-45deg);
    }
  `}
`;
export const BrickCountDisplay = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  text-align: center;
`;

export const GameWrapper = styled.div`
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
